import ApplyButton from "components/ApplyNow/ApplyButton";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useAppSelector } from "store/hooks";
import { getSettings } from "store/slices";
import { twMerge } from "tailwind-merge";

// Helper to evaluate conditions
const evaluateCondition = (condition, values) => {
  if (!condition) return true;
  if (condition.operator) {
    const results = Object.values(condition)
      .filter((v) => typeof v === "object" && v.key)
      .map((c) => values[c.key].value === c.value);
    return condition.operator === "or"
      ? results.some(Boolean)
      : results.every(Boolean);
  }
  return values[condition.key]?.value === condition.value;
};

const RepeatableGroup = ({ groupDef, values, setValues, fieldKey, tabKey }) => {
  let items = values[`${tabKey}|${fieldKey}`] || [{}];
  if (!Array.isArray(items)) {
    items = [{}];
  }
  const minItems = groupDef.min_items || 1;
  const maxItems = groupDef.max_items || 5;

  const handleChange = (idx, key, value) => {
    const updated = items.map((item, i) =>
      i === idx ? { ...item, [key]: value } : item
    );
    setValues((v) => ({ ...v, [`${tabKey}|${fieldKey}`]: updated }));
  };

  return (
    <div className="my-4">
      <label className="block font-semibold mb-2">
        {groupDef.title?.title || "Subjects"}
      </label>
      {items.map((item, idx) => (
        <div key={idx} className="flex gap-2 items-center mb-2">
          {/* Subject select */}
          <select
            value={item.label || ""}
            onChange={(e) => handleChange(idx, "label", e.target.value)}
            className="border rounded px-2 py-1"
          >
            <option value="">Select subject</option>
            {groupDef.title.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {/* Units */}
          <select
            value={item.units || ""}
            onChange={(e) => handleChange(idx, "units", e.target.value)}
            className="border rounded px-2 py-1"
          >
            <option value="">Units</option>
            {groupDef.single_choice.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {/* Grade */}
          <input
            type="number"
            min={groupDef.int_input.min}
            max={groupDef.int_input.max}
            value={item.grade || ""}
            onChange={(e) => {
              let tempValue = parseFloat(e.target.value);
              if (tempValue > groupDef.int_input.max) {
                tempValue = groupDef.int_input.max;
              }
              if (tempValue < groupDef.int_input.min) {
                tempValue = groupDef.int_input.min;
              }
              handleChange(idx, "grade", tempValue);
            }}
            className="border rounded px-2 py-1 w-20"
            placeholder="Grade"
          />
          {/* Remove button */}
          {items.length > minItems && (
            <button
              type="button"
              className="text-red-500"
              onClick={() => {
                const updated = items.filter((_, i) => i !== idx);
                setValues((v) => ({
                  ...v,
                  [`${tabKey}|${fieldKey}`]: updated,
                }));
              }}
            >
              Remove
            </button>
          )}
        </div>
      ))}
      {/* Add button */}
      {items.length < maxItems && (
        <button
          type="button"
          className="mt-2 px-3 py-1 bg-theme-blue text-white rounded"
          onClick={() =>
            setValues((v) => ({
              ...v,
              [`${tabKey}|${fieldKey}`]: [...items, {}],
            }))
          }
        >
          Add
        </button>
      )}
    </div>
  );
};

const DynamicFields = ({ fields, values, setValues, tabKey }) => {
  return Object.entries(fields).map(([fieldKey, field]) => {
    // Handle conditional fields
    if (
      field.type === "conditional" &&
      !evaluateCondition(field.conditions, values)
    ) {
      return null;
    }

    // Handle conditional_group (based on another field's value)
    if (field.conditional_group) {
      // Find the dependency field and its value
      const dependsOn = field.conditions?.depends_on;
      const [depTab, depField] = dependsOn?.split("|") || [];
      const depValue = values[`${depTab}|${depField}`]?.value;
      const groupFields = field.conditional_group[depValue]?.fields || [];
      if (!depValue || !groupFields.length) return null;
      return (
        <div key={fieldKey} className="my-4">
          <label className="block font-semibold mb-2">{field.title}</label>
          {groupFields.map((subField, idx) => (
            <div key={idx} className="mb-2">
              {/* Render single_choice for units */}
              {subField.single_choice && (
                <div className="mb-1">
                  <label className="mr-2">{subField.title} :</label>
                  <select
                    value={values[`${tabKey}|${fieldKey}_${idx}`]?.units || ""}
                    placeholder={subField.single_choice.title}
                    onChange={(e) =>
                      setValues((v) => ({
                        ...v,
                        [`${tabKey}|${fieldKey}_${idx}`]: {
                          ...values[`${tabKey}|${fieldKey}_${idx}`],
                          units: e.target.value,
                          label: subField.title,
                        },
                      }))
                    }
                    className="border rounded px-2 py-1"
                  >
                    <option value="" disabled>
                      {subField.single_choice.title}
                    </option>
                    {subField.single_choice.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  {/* Render int_input for grade */}
                  {subField.int_input && (
                    <>
                      <input
                        type="number"
                        placeholder={subField.int_input.title}
                        min={subField.int_input.range?.min}
                        max={subField.int_input.range?.max}
                        value={
                          values[`${tabKey}|${fieldKey}_${idx}`]?.grade || ""
                        }
                        onChange={(e) => {
                          let tempValue = parseFloat(e.target.value);
                          if (tempValue > subField.int_input.range?.max) {
                            tempValue = subField.int_input.range?.max;
                          }
                          if (tempValue <= subField.int_input.range?.min) {
                            tempValue = subField.int_input.range?.min;
                          }
                          setValues((v) => ({
                            ...v,
                            [`${tabKey}|${fieldKey}_${idx}`]: {
                              ...values[`${tabKey}|${fieldKey}_${idx}`],
                              grade: tempValue,
                              label: subField.title,
                            },
                          }));
                        }}
                        className="border rounded px-2 py-1 w-20 ml-2"
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Handle repeatable_group
    if (field.repeatable_group) {
      return (
        <RepeatableGroup
          tabKey={tabKey}
          key={fieldKey}
          groupDef={field.repeatable_group}
          values={values}
          setValues={setValues}
          fieldKey={fieldKey}
        />
      );
    }

    // Single choice field
    if (field.single_choice) {
      return (
        <div key={fieldKey} className="my-4">
          <label className="block font-semibold mb-2">
            {field.single_choice.title}
          </label>
          <div className="flex gap-4 flex-wrap">
            {field.single_choice.options.map((option) => (
              <button
                key={option}
                type="button"
                className={twMerge(
                  "px-4 py-2 border rounded",
                  values[`${tabKey}|${fieldKey}`]?.value === option
                    ? "bg-theme-blue text-white"
                    : "bg-white"
                )}
                onClick={() =>
                  setValues((v) => ({
                    ...v,
                    [`${tabKey}|${fieldKey}`]: {
                      value: option,
                      label: field.single_choice.title,
                    },
                  }))
                }
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Integer input field
    if (field.int_input) {
      return (
        <div key={fieldKey} className="my-4">
          <label className="block font-semibold mb-2">
            {field.int_input.title}
          </label>
          <input
            type="number"
            min={field.int_input.range?.min}
            max={field.int_input.range?.max}
            value={values[`${tabKey}|${fieldKey}`]?.value || ""}
            onBlur={(e) => {
              let tempValue = parseFloat(e.target.value);
              if (tempValue > field.int_input.range?.max) {
                tempValue = field.int_input.range?.max;
              }
              if (tempValue < field.int_input.range?.min) {
                tempValue = field.int_input.range?.min;
              }
              setValues((v) => ({
                ...v,
                [`${tabKey}|${fieldKey}`]: {
                  value: tempValue,
                  label: field.int_input.title,
                },
              }));
            }}
            onChange={(e) => {
              setValues((v) => ({
                ...v,
                [`${tabKey}|${fieldKey}`]: {
                  value: e.target.value,
                  label: field.int_input.title,
                },
              }));
            }}
            className="border px-3 py-2 rounded w-32"
          />
        </div>
      );
    }

    return null;
  });
};
// ...existing code...
const ApplicationForm = ({ applicationForm, onsubmit }) => {
  const [currentTab, setCurrentTab] = useState(
    Object.keys(applicationForm.details)[0]
  );
  // Store all field values
  const [fieldValues, setFieldValues] = useState({});
  const [fieldsToRender, setFieldsToRender] = useState({});

  const { dir } = useAppSelector(getSettings);

  useEffect(() => {
    // Initialize field values based on the current tab
    const initialValues = Object.fromEntries(
      Object.entries(applicationForm.details[currentTab]).filter(([k]) =>
        k.startsWith("field_")
      )
    );
    const pickDefaultsValues = Object.fromEntries(
      Object.entries(applicationForm.details[currentTab])
        .map(([k, v]) => {
          if (
            k.startsWith("field_") &&
            (v?.single_choice?.default || v?.int_input?.default)
          ) {
            return [
              `${currentTab}|${k}`,
              {
                value: v?.single_choice?.default || v?.int_input?.default,
                label: v?.single_choice?.title || v?.int_input?.title,
              },
            ];
          } else {
            return "";
          }
        })
        .filter((ele) => ele)
    );

    // Set initial values for fields that have defaults
    setFieldValues((prev) => ({
      ...pickDefaultsValues,
      ...prev,
    }));
    // Set fields to render based on the current tab
    setFieldsToRender(initialValues);
  }, [currentTab, applicationForm.details]);

  return (
    <div className="mt-3 container" dir={dir}>
      <h1 className="text-3xl text-theme-blue font-bold text-center">
        {applicationForm.title
          ? `${applicationForm.title}`
          : `Let's Do a Quick Check`}
      </h1>
      <p className="text-theme-blue text-xl font-light mt-1 text-center">
        {applicationForm.subtitle || "To ensure you can apply for this major"}
      </p>
      {Object.keys(applicationForm.details).length > 1 ? (
        <div className="flex items-center mt-8" dir={dir}>
          {Object.keys(applicationForm.details).map((key) => {
            const detail = applicationForm.details[key];
            return (
              <button
                key={key}
                className={twMerge(
                  "w-full p-4 text-lg font-semibold text-theme-blue transition-all duration-300 ease-in-out",
                  currentTab === key ? "bg-theme-gray" : ""
                )}
                onClick={() => setCurrentTab(key)}
                type="button"
                aria-label={`Switch to ${detail.title} tab`}
                title={`Switch to ${detail.title} tab`}
              >
                {detail.title}
              </button>
            );
          })}
        </div>
      ) : (
        ""
      )}
      <p className="text-center">
        {applicationForm.details[currentTab].subtitle}
      </p>
      {applicationForm.details[currentTab].reference ? (
        <a
          href={applicationForm.details[currentTab].reference?.link}
          target="_blank"
          rel="noreferrer"
          className="text-center block underline mt-2"
        >
          {applicationForm.details[currentTab].reference?.text}
        </a>
      ) : null}
      <div className="mt-4 py-4">
        {/* Render dynamic fields for the first tab as an example */}
        <div className="px-4 sm:px-10 lg:px-40" dir={dir}>
          {applicationForm.details[currentTab] ? (
            <DynamicFields
              fields={fieldsToRender}
              values={fieldValues}
              setValues={setFieldValues}
              tabKey={currentTab}
            />
          ) : (
            ""
          )}
        </div>
        <div className={`mt-6 flex gap-4 w-full justify-end ${
          Object.keys(applicationForm.details).indexOf(currentTab) ===
              Object.keys(applicationForm.details).length - 1 ? "flex-col-reverse" : "flex-row"
        }`} dir={dir}>
          {/* Back button (not on first tab) */}
          {Object.keys(applicationForm.details).indexOf(currentTab) > 0 && (
              <button
                className={`bg-gray-200 text-theme-blue py-2 px-6 rounded hover:bg-gray-300 transition-colors w-fit ${
                  Object.keys(applicationForm.details).indexOf(currentTab) ===
              Object.keys(applicationForm.details).length - 1 ? "mx-auto w-full md:w-72 rounded-2xl" : ""
                }`}
                onClick={() => {
                  const tabKeys = Object.keys(applicationForm.details);
                  const currentIdx = tabKeys.indexOf(currentTab);
                  if (currentIdx > 0) setCurrentTab(tabKeys[currentIdx - 1]);
                }}
                type="button"
              >
                {t("applicationFormBackButton")}
              </button>
            )}

          {/* Next button (not on last tab) */}
          {Object.keys(applicationForm.details).indexOf(currentTab) <
            Object.keys(applicationForm.details).length - 1 && (
            <button
              className="px-6  bg-theme-blue text-white py-2 rounded hover:bg-theme-blue-dark transition-colors"
              onClick={() => {
                const tabKeys = Object.keys(applicationForm.details);
                const currentIdx = tabKeys.indexOf(currentTab);
                if (currentIdx < tabKeys.length - 1)
                  setCurrentTab(tabKeys[currentIdx + 1]);
              }}
              type="button"
            >
              {t("applicationFormNextButton")}
            </button>
          )}
          {/* Submit button (only on last tab) */}
          {Object.keys(applicationForm.details).indexOf(currentTab) ===
            Object.keys(applicationForm.details).length - 1 && (
            <ApplyButton
              className="mt-14 w-full md:w-72 mx-auto"
              text={t("applicationFormSubmitButton")}
              onClick={() => onsubmit(fieldValues)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationForm;
